import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "react-toastify";

import {
  Avatar,
  Badge,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  InputAdornment,
  List,
  ListItemAvatar,
  ListItemButton,
  ListItemText,
  TextField,
  Typography
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";

import toastError from "../../errors/toastError";
import api from "../../services/api";

const initials = name =>
  (name || "?")
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map(part => part[0])
    .join("")
    .toUpperCase();

// Manda o ticket para um colega pelo chat interno, com recado. Diferente da
// transferência de fila: o atendimento continua com quem está, o que vai é o
// pedido de ajuda — com o contexto junto.
const ForwardTicketToChatModal = ({ open, onClose, ticketId }) => {
  const navigate = useNavigate();
  const [users, setUsers] = useState([]);
  const [selected, setSelected] = useState(null);
  const [search, setSearch] = useState("");
  const [note, setNote] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    if (!open) return;
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/internal-chats/users");
        setUsers(data);
      } catch (err) {
        toastError(err);
      }
    };
    fetchUsers();
    setSelected(null);
    setSearch("");
    setNote("");
  }, [open]);

  const handleSend = async () => {
    if (!selected) {
      toast.warn("Escolha para quem enviar.");
      return;
    }
    setSending(true);
    try {
      const { data } = await api.post("/internal-chats/forward-ticket", {
        userId: selected,
        ticketId,
        body: note.trim()
      });
      toast.success("Enviado para o chat interno.");
      onClose();
      // Abre a conversa na própria tela de Atendimentos (aba "Chat interno"),
      // em vez de jogar o atendente para outra página.
      navigate(`/tickets?interno=${data.chat.id}`);
    } catch (err) {
      toastError(err);
    } finally {
      setSending(false);
    }
  };

  const filtered = users.filter(u =>
    u.name?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <Dialog open={open} onClose={onClose} fullWidth maxWidth="xs">
      <DialogTitle>Enviar ticket #{ticketId} para a equipe</DialogTitle>
      <DialogContent dividers>
        <TextField
          placeholder="Buscar atendente"
          fullWidth
          size="small"
          value={search}
          onChange={e => setSearch(e.target.value)}
          InputProps={{
            startAdornment: (
              <InputAdornment position="start">
                <SearchIcon fontSize="small" />
              </InputAdornment>
            )
          }}
          sx={{ mb: 1 }}
        />

        <List dense sx={{ maxHeight: 240, overflowY: "auto" }}>
          {filtered.map(user => (
            <ListItemButton
              key={user.id}
              selected={selected === user.id}
              onClick={() => setSelected(user.id)}
            >
              <ListItemAvatar>
                <Badge
                  color="success"
                  variant="dot"
                  overlap="circular"
                  invisible={!user.online}
                >
                  <Avatar sx={{ width: 34, height: 34, fontSize: 14 }}>
                    {initials(user.name)}
                  </Avatar>
                </Badge>
              </ListItemAvatar>
              <ListItemText
                primary={user.name}
                secondary={user.online ? "Online" : "Offline"}
              />
            </ListItemButton>
          ))}
          {!filtered.length && (
            <Typography variant="body2" color="textSecondary" sx={{ p: 2 }}>
              Nenhum atendente encontrado.
            </Typography>
          )}
        </List>

        <TextField
          label="Recado (opcional)"
          placeholder="Ex.: cliente pergunta sobre a nota fiscal, pode assumir?"
          fullWidth
          multiline
          rows={3}
          size="small"
          value={note}
          onChange={e => setNote(e.target.value)}
          sx={{ mt: 1 }}
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose} color="secondary" disabled={sending}>
          Cancelar
        </Button>
        <Button onClick={handleSend} variant="contained" disabled={sending}>
          {sending ? <CircularProgress size={20} /> : "Enviar"}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ForwardTicketToChatModal;
